/* ============================================================
   SlicingSection — 16215cbc: print-file preparation on the
   provider order page. The provider picks one of their printers
   (with configured slicer presets) and one of the order's model
   files; the platform slices it headless (Bambu Studio CLI) and
   attaches the .gcode.3mf artifact for download.
   ============================================================ */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { api } from '../api/client';
import type { SlicingContext, SlicingJob } from '../types/slicing';
import {
  SLICING_STATUS_LABELS,
  normalizeSlicingJobStatus,
  slicingStatusBadgeClass,
} from '../types/slicing';
import {
  plateLabel,
  plateOptions,
  printerCanSliceModels,
  printerProfileHint,
} from './slicingPlatesView';

const POLL_INTERVAL_MS = 10_000;

const selectStyle: CSSProperties = {
  padding: '10px 14px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-separator)',
  fontSize: 'var(--fs-body)',
  fontFamily: 'inherit',
  background: 'var(--color-surface)',
  color: 'var(--color-text-primary)',
};

function formatMinutesRu(min?: number): string {
  if (min == null || min <= 0) return '—';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h} ч ${m} мин` : `${m} мин`;
}

export function SlicingSection({ orderId }: { orderId: string }) {
  const [context, setContext] = useState<SlicingContext | null>(null);
  const [jobs, setJobs] = useState<SlicingJob[]>([]);
  const [printerId, setPrinterId] = useState('');
  const [modelFileId, setModelFileId] = useState('');
  const [plateIndex, setPlateIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const orderIdRef = useRef(orderId);

  const refresh = useCallback(async (id: string) => {
    try {
      const [ctx, jobList] = await Promise.all([
        api.getSlicingContext(id),
        api.listSlicingJobs(id),
      ]);
      setContext(ctx);
      setJobs(jobList);
      setLoadFailed(false);
    } catch {
      // Order not visible to this provider — hide the block entirely.
      setLoadFailed(true);
    }
  }, []);

  useEffect(() => {
    orderIdRef.current = orderId;
    setContext(null);
    setJobs([]);
    setPrinterId('');
    setModelFileId('');
    setPlateIndex(0);
    setError(null);
    void refresh(orderId);
  }, [orderId, refresh]);

  // Poll while a job is queued/running so the artifact appears without a
  // manual reload (bounded: one interval, cleared when nothing is active).
  const hasActive = jobs.some(
    (j) => ['Pending', 'Running'].includes(normalizeSlicingJobStatus(j.status)),
  );
  useEffect(() => {
    if (!hasActive) return;
    const timer = window.setInterval(() => {
      void api.listSlicingJobs(orderIdRef.current).then(setJobs).catch(() => undefined);
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [hasActive, jobs]);

  // First printer with profiles wins as the default pick.
  useEffect(() => {
    if (context && !printerId) {
      const withProfiles = context.printers.find((p) => p.hasProfiles) ?? context.printers[0];
      if (withProfiles) setPrinterId(withProfiles.printerId);
    }
    if (context && context.modelFiles.length > 0 && !modelFileId) {
      setModelFileId(context.modelFiles[0].modelFileId);
    }
    // 61454143: reset the plate pick when the file selection changes underneath.
    if (context && modelFileId && !context.modelFiles.some((f) => f.modelFileId === modelFileId)) {
      setModelFileId(context.modelFiles[0]?.modelFileId ?? '');
      setPlateIndex(0);
    }
  }, [context, printerId, modelFileId]);

  if (loadFailed) return null;

  const noFiles = (context?.modelFiles.length ?? 0) === 0;
  const noPrinters = (context?.printers.length ?? 0) === 0;
  // Quiet on orders where preparation is impossible and nothing ran yet —
  // the block appears once the order is accepted or a job exists.
  if (!context || ((noFiles || noPrinters || !context.canCreateJobs) && jobs.length === 0)) {
    return null;
  }

  const createJob = async (exportKind: 'gcode' | '3mf') => {
    if (!printerId || !modelFileId) return;
    setBusy(true);
    setError(null);
    try {
      await api.createSlicingJob(orderId, printerId, modelFileId, plateIndex, exportKind);
      setJobs(await api.listSlicingJobs(orderId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось поставить задачу');
    } finally {
      setBusy(false);
    }
  };

  const saveBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const download = async (job: SlicingJob) => {
    try {
      const blob = await api.downloadSlicingArtifact(job.jobId);
      const base = job.modelFileName.replace(/\.[^.]+$/, '');
      // 1a6b6f55: project exports arrive as .3mf; sliced jobs as .gcode.3mf.
      saveBlob(blob, job.exportKind === '3mf' ? `${base}.3mf` : `${base}.gcode.3mf`);
    } catch {
      setError('Не удалось скачать файл');
    }
  };

  // 1a6b6f55: «сырая модель» — the original upload, byte-for-byte as the
  // customer sent it (STL/OBJ/3MF/STEP), via the viewer-content endpoint.
  const downloadRaw = async () => {
    if (!modelFileId || !selectedFile) return;
    setBusy(true);
    setError(null);
    try {
      const blob = await api.downloadModelFile(modelFileId);
      saveBlob(blob, selectedFile.fileName);
    } catch {
      setError('Не удалось скачать файл');
    } finally {
      setBusy(false);
    }
  };

  const selectedPrinter = context.printers.find((p) => p.printerId === printerId);
  const selectedFile = context.modelFiles.find((f) => f.modelFileId === modelFileId);
  const plates = plateOptions(selectedFile);
  // 1a6b6f55: without a server slicer the queue buttons would be dead —
  // show the honest state and keep only the raw-model download enabled.
  const slicerReady = context.slicerAvailable !== false;
  const queueDisabled = busy || !printerId || !modelFileId || !printerCanSliceModels(selectedPrinter);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)', paddingTop: 'var(--space-sm)', borderTop: '1px solid var(--color-separator)' }}>
      <p style={{ fontSize: 'var(--fs-overline)', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Подготовка файлов печати
      </p>

      {context.canCreateJobs && !noFiles && !noPrinters && (
        <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
          {context.modelFiles.length > 1 && (
            <select style={selectStyle} value={modelFileId} onChange={(e) => setModelFileId(e.target.value)}>
              {context.modelFiles.map((f) => (
                <option key={f.modelFileId} value={f.modelFileId}>{f.fileName}</option>
              ))}
            </select>
          )}
          <button
            className="btn btn-tertiary"
            disabled={busy || !modelFileId}
            onClick={() => void downloadRaw()}
          >
            ⬇ Исходная модель
          </button>
          <select style={{ ...selectStyle, minWidth: 200 }} value={printerId} onChange={(e) => setPrinterId(e.target.value)}>
            {context.printers.map((p) => (
              <option key={p.printerId} value={p.printerId}>
                {p.name} · {p.brand} {p.model}{printerCanSliceModels(p) ? '' : ' (без профилей)'}
              </option>
            ))}
          </select>
          {plates.length > 0 && (
            <select style={selectStyle} value={plateIndex} onChange={(e) => setPlateIndex(Number(e.target.value))}>
              <option value={0}>{plateLabel(0)}</option>
              {plates.map((n) => (
                <option key={n} value={n}>{plateLabel(n)}</option>
              ))}
            </select>
          )}
          <button
            className="btn btn-secondary"
            disabled={queueDisabled || !slicerReady}
            title={slicerReady ? undefined : 'Слайсер недоступен на сервере'}
            onClick={() => void createJob('gcode')}
          >
            {busy ? '⏳ …' : '⚙ G-code'}
          </button>
          <button
            className="btn btn-secondary"
            disabled={queueDisabled || !slicerReady}
            title={slicerReady ? undefined : 'Слайсер недоступен на сервере'}
            onClick={() => void createJob('3mf')}
          >
            {busy ? '⏳ …' : '📦 3MF'}
          </button>
        </div>
      )}

      {context.canCreateJobs && !slicerReady && (
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>
          Слайсер (Bambu Studio CLI) не установлен на сервере — серверная подготовка 3MF и g-code недоступна.
          Скачайте исходную модель и слайсите локально, либо сообщите в поддержку.
        </p>
      )}

      {context.canCreateJobs && printerProfileHint(selectedPrinter) != null && (
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
          {printerProfileHint(selectedPrinter)}
        </p>
      )}

      {error && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>}

      {jobs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
          {jobs.map((job) => {
            const status = normalizeSlicingJobStatus(job.status);
            return (
              <div
                key={job.jobId}
                style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'center', flexWrap: 'wrap', fontSize: 'var(--fs-caption)' }}
              >
                <span className={slicingStatusBadgeClass(status)}>{SLICING_STATUS_LABELS[status]}</span>
                <span>{job.printerName}</span>
                <span style={{ color: 'var(--color-text-secondary)' }}>{job.modelFileName}</span>
                {job.plateIndex != null && job.plateIndex > 0 && (
                  <span style={{ color: 'var(--color-text-secondary)' }}>{plateLabel(job.plateIndex)}</span>
                )}
                {job.result && job.exportKind !== '3mf' && (
                  <span style={{ color: 'var(--color-text-secondary)' }}>
                    ~{formatMinutesRu(job.result.estimatedPrintMinutes)} · {Math.round(job.result.totalFilamentGrams ?? 0)} г
                    {job.result.layerCount ? ` · ${job.result.layerCount} слоёв` : ''}
                  </span>
                )}
                {job.exportKind === '3mf' && (
                  <span style={{ color: 'var(--color-text-secondary)' }}>проект 3MF с настройками</span>
                )}
                {job.hasArtifact && (
                  <button className="btn btn-tertiary" style={{ fontSize: 'var(--fs-caption)' }} onClick={() => void download(job)}>
                    ⬇ {job.exportKind === '3mf' ? '3mf' : 'gcode.3mf'}
                  </button>
                )}
                {status === 'Failed' && job.errorMessage && (
                  <span style={{ color: 'var(--color-danger)' }}>{job.errorMessage}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
